import type React from 'react';
import { useCallback, useState, useEffect } from 'react';

import { VoicemailRounded } from '@mui/icons-material';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { trackAnalyticsEvent } from '@ragu-components/ord-analytics';
import { OrdFormList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { dataSchema } from '@ragu-components/ord-types';
import {
  BooleanInput,
  HttpError,
  NumberInput,
  PasswordInput,
  regex,
  required,
  ResourceContextProvider,
  SaveContextProvider,
  SimpleForm,
  useNotify,
  useTranslate,
} from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { SimpleSaveToolbar } from 'src/components/forms';

import type { Voicemail } from '@ragu-components/ord-types';
import type { FieldValues } from 'react-hook-form';

const VOICEMAIL_PIN_PATTERN = new RegExp(dataSchema.VoicemailPreferences.properties.pin.pattern);

export const VoicemailSettings = (): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [voicemail, setVoicemail] = useState<Voicemail | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  useEffect(() => {
    const fetchVoicemail = async (): Promise<void> => {
      try {
        const data = await dataProvider.getPersonalVoicemail();
        setVoicemail(data);
        setNotConfigured(false);
        componentLogger.info('Voicemail settings fetched', { data });
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          setNotConfigured(true);
          componentLogger.info('Voicemail not configured for user');
        } else {
          componentLogger.error('Voicemail settings fetch failed', { error });
          notify('resources.voicemails.notifications.requestError', { type: 'error' });
        }
      } finally {
        setLoading(false);
      }
    };
    void fetchVoicemail();
  }, [dataProvider, notify]);

  const handleSave = useCallback(
    (values: FieldValues) => {
      if (voicemail === null) {
        componentLogger.debug('No Voicemail settings');
        return;
      }

      const updates = { preferences: { pin: values.pin as string } };

      setSaving(true);
      dataProvider
        .updatePersonalVoicemail(updates)
        .then(() => {
          componentLogger.info('Voicemail settings updated', { updates });
          trackAnalyticsEvent('voicemail_settings_saved', { event_category: 'settings' });
          notify('resources.voicemails.notifications.updateSuccess', { type: 'info' });
        })
        .catch(() => {
          componentLogger.error('Voicemail settings update failed');
          trackAnalyticsEvent('voicemail_settings_save_failed', { event_category: 'settings' });
          notify('resources.voicemails.notifications.updateError', { type: 'error' });
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [dataProvider, notify, voicemail]
  );

  const saveContext = {
    save: handleSave,
    saving,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (notConfigured) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant='body1' color='text.secondary'>
          {translate('resources.voicemails.helperTexts.settingsNotConfigured')}
        </Typography>
      </Paper>
    );
  }

  return (
    <SaveContextProvider value={saveContext}>
      {voicemail && (
        <ResourceContextProvider value={Resources.voicemails}>
          <Paper sx={{ overflow: 'hidden' }}>
            <SimpleForm
              record={{
                pin: voicemail.preferences?.pin ?? '',
                enabled: voicemail.preferences?.enabled ?? false,
                quota: voicemail.preferences?.quota ?? '',
              }}
              onSubmit={handleSave}
              toolbar={<SimpleSaveToolbar />}
            >
              <OrdFormList icon={<VoicemailRounded />}>
                <BooleanInput source='enabled' label='resources.voicemails.fields.preferences.enabled' disabled />
                <PasswordInput
                  source='pin'
                  label='resources.voicemails.fields.preferences.pin'
                  disabled={saving}
                  autoComplete='new-password'
                  fullWidth
                  validate={[required(), regex(VOICEMAIL_PIN_PATTERN, 'resources.voicemails.validation.pin')]}
                />
                <NumberInput source='quota' label='resources.voicemails.fields.preferences.quota' disabled />
              </OrdFormList>
            </SimpleForm>
          </Paper>
        </ResourceContextProvider>
      )}
    </SaveContextProvider>
  );
};
